import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    FiAlertCircle,
    FiArrowLeft,
    FiArrowRight,
    FiBarChart2,
    FiBell,
    FiCheck,
    FiChevronDown,
    FiClock,
    FiDownload,
    FiEdit3,
    FiEye,
    FiFileText,
    FiHash,
    FiLink,
    FiList,
    FiLoader,
    FiLock,
    FiPlay,
    FiPlus,
    FiRefreshCw,
    FiRotateCcw,
    FiSave,
    FiSearch,
    FiShield,
    FiSend,
    FiShare2,
    FiTrash2,
    FiUsers,
    FiWifiOff,
    FiX,
    FiZap,
} from 'react-icons/fi';
import {examsApi, getErrorMessage} from '../services/api';
import MathText from '../components/MathText';
import {getStudentExamTiming, studentExamTimingCopy} from './examTiming';
import {getTextDirection} from './textDirection';
import {filterStudentResultGroups, getAttemptNumber, getResultGradingState, getStudentExamAvailability, groupStudentResults} from './studentExamPresentation';
import TeacherExamPanel from './TeacherExamPanel';
import {buildStudentExamShareUrl, copyTextToClipboard, formatExamShareText} from './examShare';
import {useLocation, useNavigate} from 'react-router-dom';
import useAuthRole from '../hooks/useAuthRole';
import JalaliDateTimePicker from '../components/JalaliDateTimePicker/JalaliDateTimePicker';
import StudentFeedbackHistory from '../ExamFeedback/StudentFeedbackHistory';
import StudentResultDetails from './StudentResultDetails';
import {getAutosaveRetryDelay, getSubmissionRetryAt, isAlreadyFinalized, isRetryableSubmissionError, listPendingSubmissions, loadAnswerDraft, removeAnswerDraft, removePendingSubmission, saveAnswerDraft, savePendingSubmission} from './examSubmissionRecovery';
import './ExamPage.css';
import '../ExamFeedback/ExamFeedback.css';

const emptyForm = {
    title: '',
    description: '',
    source_resource_ids: [],
    assigned_student_ids: '',
    starts_at: '',
    ends_at: '',
    duration_minutes: 45,
    selection_mode: 'manual',
    question_type: 'mixed',
    question_count: 1,
    manual_question_ids: [],
};
const statusLabels = {draft: 'پیش‌نویس', published: 'منتشرشده', closed: 'بسته‌شده'};
const typeLabels = {test: 'تستی', descriptive: 'تشریحی', mixed: 'ترکیبی'};
const toArray = (value) => Array.isArray(value) ? value : value?.results || value?.items || [];
const formatDate = (value) => value ? new Intl.DateTimeFormat('fa-IR', {
    dateStyle: 'medium',
    timeStyle: 'short'
}).format(new Date(value)) : 'تعیین نشده';
const formatDuration = (seconds) => {
    const safe = Math.max(0, seconds || 0);
    return [Math.floor(safe / 3600), Math.floor((safe % 3600) / 60), safe % 60]
        .map((part) => String(part).padStart(2, '0')).join(':');
};
const faNumberFormatter = new Intl.NumberFormat('fa-IR', {maximumFractionDigits: 2});
const formatNumber = (value, fallback = '—') => {
    if (value === null || value === undefined || value === '') return fallback;
    const number = Number(value);
    return Number.isFinite(number) ? faNumberFormatter.format(number) : fallback;
};
const questionOptions = (question) => question.options || question.distractors || [];
const releasePolicyLabels = {on_close: 'پس از بسته‌شدن', after_grading: 'پس از تصحیح کامل', scheduled: 'زمان‌بندی‌شده'};
const eventLabels = {
    tab_hidden: 'خروج از تب',
    fullscreen_exit: 'خروج از تمام‌صفحه',
    copy: 'کپی',
    paste: 'جای‌گذاری',
    camera_blocked: 'مسدودبودن دوربین',
    screenshot_shortcut: 'میان‌بر مشکوک اسکرین‌شات',
    window_blur: 'خروج focus از پنجره آزمون',
};
const notificationLabels = {upcoming: 'نزدیک‌شدن آزمون', started: 'شروع آزمون', ended: 'پایان آزمون', result: 'انتشار نتیجه'};

const downloadFile = ({blob, filename}, fallbackName) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename || fallbackName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

function Message({message}) {
    return message && <div className={`exam-message ${message.type}`}>
        {message.type === 'success' ? <FiCheck/> : <FiAlertCircle/>}<span>{message.text}</span>
    </div>;
}

function EmptyState({title, text, action}) {
    return <div className="exam-empty"><span><FiList/></span><h3>{title}</h3><p>{text}</p>{action}</div>;
}

export function LegacyTeacherPanel({exams, documents, questions, loading, reload}) {
    const navigate = useNavigate();
    const [form, setForm] = useState(emptyForm);
    const [editingExam, setEditingExam] = useState(null);
    const [detailExam, setDetailExam] = useState(null);
    const [attemptExam, setAttemptExam] = useState(null);
    const [attempts, setAttempts] = useState([]);
    const [selectedAttempt, setSelectedAttempt] = useState(null);
    const [gradeForms, setGradeForms] = useState({});
    const [saving, setSaving] = useState(false);
    const [busyKey, setBusyKey] = useState('');
    const [attemptsLoading, setAttemptsLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [questionSearch, setQuestionSearch] = useState('');
    const [insightExam, setInsightExam] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [auditEvents, setAuditEvents] = useState([]);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [accommodation, setAccommodation] = useState({student_id: '', extra_time_minutes: 0, max_attempts_override: ''});

    const availableDocuments = documents;
    const documentMap = useMemo(() => new Map(documents.map((document) => [String(document.resource_id), document])), [documents]);

    const availableQuestions = useMemo(() => {
        if (!form.source_resource_ids.length) return [];
        const term = form.selection_mode === 'manual' ? questionSearch.trim().toLowerCase() : '';
        return questions.filter((question) => {
        if (question.status && question.status !== 'ready') return false;
        if (!form.source_resource_ids.includes(String(question.resource_id))) return false;
        if (form.question_type === 'test' && !question.distractors?.length) return false;
        if (form.question_type === 'descriptive' && question.distractors?.length) return false;
        if (term) {
            const haystack = [
                question.prompt,
                question.answer,
                question.explanation,
                question.node_name,
                ...(question.distractors || []),
            ].filter(Boolean).join(' ').toLowerCase();
            if (!haystack.includes(term)) return false;
        }
        return true;
        });
    }, [form.question_type, form.selection_mode, form.source_resource_ids, questionSearch, questions]);

    const closeForm = () => {
        setEditingExam(null);
        setForm(emptyForm);
        setQuestionSearch('');
    };
    const openCreate = () => {
        navigate('/exampage/create');
    };
    const openEdit = (exam) => {
        navigate(`/exampage/${exam.id}/edit`);
    };
    const toggleSource = (id) => setForm((current) => {
        const sourceDocumentIds = current.source_resource_ids.includes(id)
            ? current.source_resource_ids.filter((item) => item !== id)
            : [...current.source_resource_ids, id];
        const validQuestionIds = new Set(questions
            .filter((question) => sourceDocumentIds.includes(String(question.resource_id)))
            .map((question) => question.id));
        return {
            ...current,
            source_resource_ids: sourceDocumentIds,
            manual_question_ids: current.manual_question_ids.filter((questionId) => validQuestionIds.has(questionId)),
        };
    });
    const toggleQuestion = (id) => setForm((current) => {
        const selected = current.manual_question_ids.includes(id) ? current.manual_question_ids.filter((item) => item !== id) : [...current.manual_question_ids, id];
        return {...current, manual_question_ids: selected, question_count: Math.max(1, selected.length)};
    });

    const saveExam = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                source_resource_ids: form.source_resource_ids,
                assigned_student_ids: form.assigned_student_ids.split(/[,\s،]+/).map(Number).filter(Number.isInteger),
                starts_at: new Date(form.starts_at).toISOString(),
                ends_at: new Date(form.ends_at).toISOString(),
                duration_minutes: Number(form.duration_minutes),
                selection_mode: form.selection_mode,
                question_type: form.question_type,
                question_count: Number(form.question_count),
            };
            if (form.selection_mode === 'manual') payload.manual_question_ids = form.manual_question_ids;
            if (editingExam.id) await examsApi.master.update(editingExam.id, payload); else await examsApi.master.create(payload);
            closeForm();
            setMessage({
                type: 'success', text: editingExam.id ? 'تغییرات آزمون ذخیره شد.' : 'آزمون به‌صورت پیش‌نویس ساخته شد.'
            });
            await reload();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setSaving(false);
        }
    };

    const runAction = async (exam, action, text) => {
        setBusyKey(`exam-${exam.id}`);
        try {
            await action(exam.id);
            setMessage({type: 'success', text});
            await reload();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };

    const shareExam = async (exam) => {
        setBusyKey(`exam-${exam.id}`);
        try {
            const result = await examsApi.master.link(exam.id);
            const shareUrl = buildStudentExamShareUrl(result);
            if (!shareUrl) throw new Error('لینک آزمون در پاسخ سرور موجود نیست.');
            const shareData = {title: exam.title, text: `کد ورود به آزمون: ${result.join_code}`, url: shareUrl};
            if (navigator.share) await navigator.share(shareData); else {
                await copyTextToClipboard(formatExamShareText(exam, result, shareUrl));
                setMessage({type: 'success', text: 'لینک و کد ورود آزمون کپی شد.'});
            }
        } catch (error) {
            if (error?.name !== 'AbortError') setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };

    const loadAttempts = async (exam, keepSelectedId = null) => {
        setAttemptExam(exam);
        setAttemptsLoading(true);
        try {
            const items = toArray(await examsApi.master.attempts(exam.id));
            setAttempts(items);
            if (keepSelectedId) setSelectedAttempt(items.find((item) => item.id === keepSelectedId) || null);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setAttemptsLoading(false);
        }
    };
    const selectAttempt = (attempt) => {
        setSelectedAttempt(attempt);
        setGradeForms(Object.fromEntries(attempt.answers.map((answer) => [answer.id, {
            awarded_points: answer.awarded_points || 0, feedback: answer.teacher_feedback || ''
        }])));
    };
    const gradeAnswer = async (answer) => {
        setBusyKey(`answer-${answer.id}`);
        try {
            await examsApi.master.gradeAnswer(attemptExam.id, answer.id, gradeForms[answer.id]);
            setMessage({type: 'success', text: 'نمره و بازخورد پاسخ ذخیره شد.'});
            await loadAttempts(attemptExam, selectedAttempt.id);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };
    const resetAttempt = async (attempt) => {
        if (!window.confirm('تلاش و تمام پاسخ‌های قبلی این دانشجو حذف شود تا بتواند دوباره در آزمون شرکت کند؟')) return;
        setBusyKey(`attempt-${attempt.id}`);
        try {
            await examsApi.master.resetAttempt(attemptExam.id, attempt.id);
            setSelectedAttempt(null);
            setMessage({type: 'success', text: 'اجازه شرکت مجدد داده شد. دانشجو می‌تواند آزمون را دوباره شروع کند.'});
            await loadAttempts(attemptExam);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };
    const openInsights = async (exam) => {
        setInsightExam(exam);
        setAnalytics(null);
        setAuditEvents([]);
        setAccommodation({student_id: '', extra_time_minutes: 0, max_attempts_override: ''});
        setInsightsLoading(true);
        try {
            const [analyticsData, eventItems] = await Promise.all([
                examsApi.master.analytics(exam.id),
                examsApi.master.events(exam.id),
            ]);
            setAnalytics(analyticsData);
            setAuditEvents(toArray(eventItems));
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setInsightsLoading(false);
        }
    };
    const exportResults = async (format) => {
        if (!insightExam) return;
        setBusyKey(`export-${format}`);
        try {
            const file = await examsApi.master.exportResults(insightExam.id, format);
            downloadFile(file, `${insightExam.title}-results.${format}`);
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };
    const saveAccommodation = async (event) => {
        event.preventDefault();
        if (!insightExam || !accommodation.student_id) return;
        setBusyKey('accommodation');
        try {
            await examsApi.master.accommodation(insightExam.id, {
                student_id: Number(accommodation.student_id),
                extra_time_minutes: Number(accommodation.extra_time_minutes || 0),
                max_attempts_override: accommodation.max_attempts_override === '' ? null : Number(accommodation.max_attempts_override),
            });
            setMessage({type: 'success', text: 'شرایط ویژه دانشجو ذخیره شد.'});
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setBusyKey('');
        }
    };
    const questionMap = new Map((attemptExam?.exam_questions || []).map((question) => [question.id, question]));

    return <>
        <section className="exam-stats">
            <article><span className="blue"><FiFileText/></span>
                <div><strong>{exams.length}</strong><small>کل آزمون‌ها</small></div>
            </article>
            <article><span className="amber"><FiEdit3/></span>
                <div><strong>{exams.filter((e) => e.status === 'draft').length}</strong><small>پیش‌نویس</small></div>
            </article>
            <article><span className="green"><FiPlay/></span>
                <div><strong>{exams.filter((e) => e.status === 'published').length}</strong><small>منتشرشده</small>
                </div>
            </article>
            <article><span className="purple"><FiUsers/></span>
                <div><strong>{exams.reduce((sum, e) => sum + (e.assigned_students?.length || 0), 0)}</strong><small>دانشجوی
                    تخصیص‌یافته</small></div>
            </article>
        </section>
        <Message message={message}/>
        <section className="exam-panel">
            <div className="exam-panel-header">
                <div><h2>آزمون‌های من</h2><p>مدیریت سؤال‌ها، دانشجویان، پاسخ‌ها و نتایج</p></div>
                <div className="exam-actions">
                    <button className="exam-btn ghost" onClick={reload}><FiRefreshCw
                        className={loading ? 'spin' : ''}/> بروزرسانی
                    </button>
                    <button className="exam-btn primary" onClick={openCreate}><FiPlus/> ساخت آزمون</button>
                </div>
            </div>
            {loading ?
                <div className="exam-loading"><FiLoader className="spin"/> در حال دریافت...</div> : exams.length ?
                    <div className="exam-grid">{exams.map((exam) => <article className="exam-card" key={exam.id}>
                        <div className="exam-card-top"><span
                            className={`exam-status ${exam.status}`}>{statusLabels[exam.status]}</span><small>#{exam.id}</small>
                        </div>
                        <h3>{exam.title}</h3><p>{exam.description || 'بدون توضیحات'}</p>
                        <div className="exam-meta">
                            <span><FiList/> {exam.question_count} سؤال</span><span><FiClock/> {exam.duration_minutes} دقیقه</span><span><FiUsers/> {exam.assigned_students?.length || 0} دانشجو</span><span><FiHash/> {typeLabels[exam.question_type]}</span><span><FiRotateCcw/> {exam.max_attempts || 1} تلاش</span>{exam.proctoring_enabled && <span><FiShield/> نظارت فعال</span>}
                        </div>
                        <div className="exam-times">
                            <span>شروع: {formatDate(exam.starts_at)}</span><span>پایان: {formatDate(exam.ends_at)}</span>
                        </div>
                        <div className="exam-card-actions">
                            <button className="exam-btn ghost" onClick={() => setDetailExam(exam)}><FiEye/> جزئیات و
                                سؤال‌ها
                            </button>
                            {exam.status === 'draft' &&
                                <button className="exam-btn ghost" onClick={() => openEdit(exam)}><FiEdit3/> ویرایش
                                </button>}
                            <button className="exam-btn ghost" disabled={busyKey === `exam-${exam.id}`}
                                    onClick={() => shareExam(exam)}><FiShare2/> اشتراک
                            </button>
                            <button className="exam-btn ghost" onClick={() => loadAttempts(exam)}><FiUsers/> پاسخ‌ها و
                                نتایج
                            </button>
                            <button className="exam-btn ghost" onClick={() => openInsights(exam)}><FiBarChart2/> تحلیل و نظارت</button>
                            {exam.status === 'draft' && <button className="exam-btn success"
                                                                onClick={() => runAction(exam, examsApi.master.publish, 'آزمون منتشر شد.')}>
                                <FiPlay/> انتشار</button>}
                            {exam.status === 'published' && <button className="exam-btn warning"
                                                                    onClick={() => runAction(exam, examsApi.master.close, 'آزمون بسته شد.')}>
                                <FiLock/> بستن</button>}
                            {exam.status === 'draft' && <button className="exam-icon danger"
                                                                onClick={() => window.confirm('این آزمون حذف شود؟') && runAction(exam, examsApi.master.remove, 'آزمون حذف شد.')}>
                                <FiTrash2/></button>}
                        </div>
                    </article>)}</div> :
                    <EmptyState title="هنوز آزمونی ساخته نشده" text="اولین آزمون را از بانک سؤال بسازید."
                                action={<button className="exam-btn primary" onClick={openCreate}><FiPlus/> ساخت آزمون
                                </button>}/>}
        </section>

        {editingExam && <div className="exam-overlay" onClick={() => !saving && closeForm()}>
            <form className="exam-modal exam-create" onSubmit={saveExam} onClick={(e) => e.stopPropagation()}>
                <div className="exam-modal-header">
                    <div><span>{editingExam.id ? <FiEdit3/> : <FiPlus/>}</span>
                        <div><h2>{editingExam.id ? 'ویرایش آزمون' : 'ساخت آزمون جدید'}</h2><p>اطلاعات، دانشجویان و
                            سؤال‌ها را مدیریت کنید.</p></div>
                    </div>
                    <button type="button" onClick={closeForm}><FiX/></button>
                </div>
                <div className="exam-modal-body">
                    <div className="exam-form">
                        <label className="wide"><span>عنوان آزمون *</span><input required value={form.title}
                                                                                 onChange={(e) => setForm({
                                                                                     ...form, title: e.target.value
                                                                                 })}/></label>
                        <label className="wide"><span>توضیحات</span><textarea rows="3" value={form.description}
                                                                              onChange={(e) => setForm({
                                                                                  ...form, description: e.target.value
                                                                              })}/></label>
                        <label><span>نوع سؤال</span><select value={form.question_type} onChange={(e) => setForm({
                            ...form, question_type: e.target.value, manual_question_ids: []
                        })}>
                            <option value="mixed">ترکیبی</option>
                            <option value="test">تستی</option>
                            <option value="descriptive">تشریحی</option>
                        </select></label>
                        <JalaliDateTimePicker label="شروع آزمون" required value={form.starts_at}
                                             onChange={(value) => setForm({...form, starts_at: value})}/>
                        <JalaliDateTimePicker label="پایان آزمون" required minValue={form.starts_at}
                                             value={form.ends_at} onChange={(value) => setForm({...form, ends_at: value})}/>
                        <label><span>مدت (دقیقه)</span><input required min="1" type="number"
                                                              value={form.duration_minutes} onChange={(e) => setForm({
                            ...form, duration_minutes: e.target.value
                        })}/></label>
                        <label><span>روش انتخاب</span><select value={form.selection_mode} onChange={(e) => setForm({
                            ...form, selection_mode: e.target.value, manual_question_ids: []
                        })}>
                            <option value="manual">دستی</option>
                            <option value="random">تصادفی</option>
                        </select></label>
                        <label className="wide"><span>شناسه دانشجویان <small>با ویرگول جدا کنید</small></span><input
                            value={form.assigned_student_ids}
                            onChange={(e) => setForm({...form, assigned_student_ids: e.target.value})}/></label>
                    </div>
                    <div className="exam-picker">
                        <div><strong>منابع آزمون</strong><small>حداقل یک منبع انتخاب کنید</small></div>
                        <div className="source-grid">{availableDocuments.map((resource) => <label key={resource.resource_id}
                                                                                     className={form.source_resource_ids.includes(String(resource.resource_id)) ? 'selected' : ''}><input
                            type="checkbox" checked={form.source_resource_ids.includes(String(resource.resource_id))}
                            onChange={() => toggleSource(String(resource.resource_id))}/><FiFileText/><span>{resource.original_name || resource.title}</span></label>)}</div>
                        {!availableDocuments.length && <div className="picker-empty">منبعی برای انتخاب ثبت نشده است.</div>}
                    </div>
                    {form.selection_mode === 'manual' ? <div className="exam-picker">
                        <div><strong>سؤال‌ها</strong><small>{form.manual_question_ids.length} انتخاب شده</small></div>
                        {form.source_resource_ids.length > 0 && <label className="question-search">
                            <FiSearch/>
                            <input value={questionSearch} onChange={(event) => setQuestionSearch(event.target.value)}
                                   placeholder="جستجو در سؤال، پاسخ یا گزینه‌ها..."/>
                        </label>}
                        {!form.source_resource_ids.length ? <div className="picker-empty prominent">
                            <FiFileText/>
                            <strong>ابتدا منبع آزمون را انتخاب کنید</strong>
                            <span>بعد از انتخاب منبع، فقط سؤال‌های همان منبع نمایش داده می‌شوند.</span>
                        </div> : availableQuestions.length ? <div className="question-picker">{availableQuestions.map((question) => <label key={question.id}
                                                                                                       className={form.manual_question_ids.includes(question.id) ? 'selected' : ''}><input
                            type="checkbox" checked={form.manual_question_ids.includes(question.id)}
                            onChange={() => toggleQuestion(question.id)}/>
                            <div>
                                <strong><MathText text={question.prompt}/></strong><small>{question.distractors?.length ? 'تستی' : 'تشریحی'} ·
                                {documentMap.get(String(question.resource_id))?.original_name || `منبع ${question.resource_id}`}</small></div>
                        </label>)}</div> : <div className="picker-empty">سؤالی مطابق فیلترهای فعلی پیدا نشد.</div>}
                    </div> : <label className="random-count"><span>تعداد سؤال تصادفی</span><input min="1" max={Math.max(1, availableQuestions.length)} type="number"
                                                                                                  value={form.question_count}
                                                                                                  onChange={(e) => setForm({
                                                                                                      ...form,
                                                                                                      question_count: e.target.value
                                                                                                  })}/><small>{availableQuestions.length} سؤال
                        آماده موجود است.</small></label>}
                </div>
                <div className="exam-modal-footer">
                    <button type="button" className="exam-btn ghost" onClick={closeForm}>انصراف</button>
                    <button className="exam-btn primary"
                            disabled={saving
                                || !form.source_resource_ids.length
                                || (form.selection_mode === 'manual' && !form.manual_question_ids.length)
                                || (form.selection_mode === 'random' && (!availableQuestions.length || Number(form.question_count) > availableQuestions.length))}>{saving ?
                        <FiLoader className="spin"/> : <FiSave/>} ذخیره آزمون
                    </button>
                </div>
            </form>
        </div>}

        {detailExam && <div className="exam-overlay" onClick={() => setDetailExam(null)}>
            <div className="exam-modal exam-wide" onClick={(e) => e.stopPropagation()}>
                <div className="exam-modal-header">
                    <div><span><FiList/></span>
                        <div><h2>{detailExam.title}</h2><p>{detailExam.exam_questions?.length || 0} سؤال
                            · {statusLabels[detailExam.status]}</p></div>
                    </div>
                    <button onClick={() => setDetailExam(null)}><FiX/></button>
                </div>
                <div className="exam-modal-body">
                    <div className="exam-detail-summary">
                        <span>منابع: {detailExam.source_resources?.length || 0}</span><span>مدت: {detailExam.duration_minutes} دقیقه</span><span>دانشجویان: {detailExam.assigned_students?.length || 0}</span>
                    </div>
                    <div className="exam-question-list">{detailExam.exam_questions?.map((question, index) => <article
                        key={question.id}>
                        <header>
                            <b>سؤال {index + 1}</b><span>{question.grading_type === 'auto' ? 'تستی' : 'تشریحی'} · {question.points} نمره</span>
                        </header>
                        <h3><MathText text={question.prompt}/></h3>{question.distractors?.length > 0 &&
                        <div className="question-options">{[question.answer, ...question.distractors].map((option) =>
                            <span key={option}
                                  className={option === question.answer ? 'correct' : ''}><MathText text={option}/></span>)}</div>}
                        <div className="correct-answer"><strong>پاسخ صحیح:</strong> <MathText text={question.answer}/></div>
                        {question.explanation && <p><MathText text={question.explanation}/></p>}</article>)}</div>
                </div>
            </div>
        </div>}

        {insightExam && <div className="exam-overlay" onClick={() => setInsightExam(null)}>
            <div className="exam-modal exam-wide" onClick={(event) => event.stopPropagation()}>
                <div className="exam-modal-header">
                    <div><span><FiBarChart2/></span><div><h2>تحلیل و نظارت {insightExam.title}</h2><p>آمار مشارکت، خروجی نتایج، شرایط ویژه و رخدادهای مراقبتی</p></div></div>
                    <button onClick={() => setInsightExam(null)}><FiX/></button>
                </div>
                <div className="exam-modal-body">{insightsLoading ? <div className="exam-loading"><FiLoader className="spin"/> در حال دریافت گزارش...</div> : <>
                    <div className="analytics-cards">
                        <div><strong>{analytics?.assigned_count ?? 0}</strong><span>تخصیص‌یافته</span></div>
                        <div><strong>{analytics?.attempt_count ?? 0}</strong><span>کل تلاش‌ها</span></div>
                        <div><strong>{analytics?.completed_count ?? 0}</strong><span>تکمیل‌شده</span></div>
                        <div><strong>{analytics?.average_score == null ? '—' : Number(analytics.average_score).toFixed(2)}</strong><span>میانگین نمره</span></div>
                        <div><strong>{analytics?.pass_rate == null ? '—' : `${analytics.pass_rate}٪`}</strong><span>نرخ قبولی</span></div>
                    </div>
                    <div className="insight-toolbar">
                        <div><strong>انتشار نتیجه:</strong> {releasePolicyLabels[insightExam.result_release_policy] || insightExam.result_release_policy}{insightExam.results_release_at && ` · ${formatDate(insightExam.results_release_at)}`}</div>
                        <button className="exam-btn ghost" disabled={busyKey === 'export-xlsx'} onClick={() => exportResults('xlsx')}><FiDownload/> Excel</button>
                        <button className="exam-btn ghost" disabled={busyKey === 'export-pdf'} onClick={() => exportResults('pdf')}><FiDownload/> PDF</button>
                    </div>
                    <form className="accommodation-form" onSubmit={saveAccommodation}>
                        <div><strong>شرایط ویژه دانشجو</strong><span>زمان اضافه یا سقف تلاش متفاوت را برای یک دانشجو اعمال کنید.</span></div>
                        <select required value={accommodation.student_id} onChange={(event) => setAccommodation({...accommodation, student_id: event.target.value})}><option value="">انتخاب دانشجو...</option>{(insightExam.assigned_students || []).map((student) => <option key={student.id} value={student.id}>{student.full_name || student.email || `دانشجو ${student.id}`}</option>)}</select>
                        <input type="number" min="0" placeholder="دقیقه اضافه" value={accommodation.extra_time_minutes} onChange={(event) => setAccommodation({...accommodation, extra_time_minutes: event.target.value})}/>
                        <input type="number" min="1" placeholder="سقف تلاش (اختیاری)" value={accommodation.max_attempts_override} onChange={(event) => setAccommodation({...accommodation, max_attempts_override: event.target.value})}/>
                        <button className="exam-btn primary" disabled={busyKey === 'accommodation'}><FiSave/> ذخیره</button>
                    </form>
                    <div className="insight-columns">
                        <section><h3>آمار سؤال‌ها</h3>{analytics?.questions?.length ? <div className="question-analytics">{analytics.questions.map((question) => <article key={question.exam_question_id}><div><strong>سؤال {question.position}</strong><span><MathText text={question.prompt}/></span></div><b>{question.correct_percentage == null ? 'بدون پاسخ' : `${question.correct_percentage}٪ صحیح`}</b><small>{question.answered_count} پاسخ · میانگین {question.average_points == null ? '—' : Number(question.average_points).toFixed(2)} نمره</small></article>)}</div> : <div className="mini-empty">هنوز داده‌ای برای تحلیل سؤال‌ها وجود ندارد.</div>}</section>
                        <section><h3>رخدادهای نظارتی</h3>{auditEvents.length ? <div className="audit-list">{auditEvents.map((item) => <article key={item.id} className={item.severity}><div><strong>{eventLabels[item.event_type] || item.event_type}</strong><span>{item.student_email || `دانشجو ${item.student_id}`}</span></div><b>{item.severity}</b><small>{formatDate(item.occurred_at)}</small></article>)}</div> : <div className="mini-empty">رخداد مشکوکی ثبت نشده است.</div>}</section>
                    </div>
                </>}</div>
            </div>
        </div>}

        {attemptExam && <div className="exam-overlay" onClick={() => {
            setAttemptExam(null);
            setSelectedAttempt(null);
        }}>
            <div className="exam-modal exam-wide" onClick={(e) => e.stopPropagation()}>
                <div className="exam-modal-header">
                    <div><span><FiUsers/></span>
                        <div><h2>پاسخ‌ها و نتایج {attemptExam.title}</h2>
                            <p>{selectedAttempt ? selectedAttempt.student_email : 'یک دانشجو را برای مشاهده پاسخ‌ها انتخاب کنید'}</p>
                        </div>
                    </div>
                    <button onClick={() => {
                        setAttemptExam(null);
                        setSelectedAttempt(null);
                    }}><FiX/></button>
                </div>
                <div className="exam-modal-body">{attemptsLoading ?
                    <div className="exam-loading"><FiLoader className="spin"/> در حال دریافت...
                    </div> : selectedAttempt ? <div className="attempt-detail">
                        <div className="attempt-detail-head">
                            <button className="exam-btn ghost" onClick={() => setSelectedAttempt(null)}>
                                <FiArrowRight/> بازگشت
                            </button>
                            <div>
                                <strong>{selectedAttempt.student_email}</strong><span>{selectedAttempt.score} از {selectedAttempt.max_score} · {selectedAttempt.status}</span>
                            </div>
                            <button className="exam-btn warning" disabled={busyKey === `attempt-${selectedAttempt.id}`}
                                    onClick={() => resetAttempt(selectedAttempt)}><FiRotateCcw/> اجازه شرکت مجدد
                            </button>
                        </div>
                        <div className="submitted-answers">{selectedAttempt.answers.map((answer, index) => {
                            const question = questionMap.get(answer.exam_question_id);
                            const formValue = gradeForms[answer.id] || {awarded_points: 0, feedback: ''};
                            return <article key={answer.id}>
                                <header><b>سؤال {index + 1}</b><span>{answer.grading_status}</span></header>
                                <h3>{question?.prompt || `سؤال ${answer.exam_question_id}`}</h3>
                                <div className="answer-compare">
                                    <div><small>پاسخ دانشجو</small><p>{answer.answer_text || 'بدون پاسخ'}</p></div>
                                    <div><small>پاسخ صحیح</small><p>{question?.answer || '-'}</p></div>
                                </div>
                                <div className="answer-score">
                                    <span>نمره: {answer.awarded_points} از {question?.points || 0}</span>{question?.grading_type === 'manual' && <>
                                    <input type="number" min="0" max={question.points} step="0.25"
                                           value={formValue.awarded_points} onChange={(e) => setGradeForms({
                                        ...gradeForms, [answer.id]: {...formValue, awarded_points: e.target.value}
                                    })}/><input placeholder="بازخورد استاد" value={formValue.feedback}
                                                onChange={(e) => setGradeForms({
                                                    ...gradeForms, [answer.id]: {...formValue, feedback: e.target.value}
                                                })}/>
                                    <button className="exam-btn primary" disabled={busyKey === `answer-${answer.id}`}
                                            onClick={() => gradeAnswer(answer)}><FiSave/> ثبت نمره
                                    </button>
                                </>}</div>
                            </article>;
                        })}</div>
                    </div> : attempts.length ?
                        <div className="attempt-list">{attempts.map((attempt) => <article key={attempt.id}>
                            <div>
                                <strong>{attempt.student_email}</strong><small>ارسال: {formatDate(attempt.submitted_at || attempt.started_at)}</small>
                            </div>
                            <span>{attempt.status}</span><b>{attempt.score} از {attempt.max_score}</b>
                            <button className="exam-btn ghost" onClick={() => selectAttempt(attempt)}><FiEye/> مشاهده
                                پاسخ‌ها
                            </button>
                            <button className="exam-btn warning" onClick={() => resetAttempt(attempt)}>
                                <FiRotateCcw/> شرکت مجدد
                            </button>
                        </article>)}</div> : <EmptyState title="تلاشی ثبت نشده"
                                                         text="پس از شروع آزمون، پاسخ‌های دانشجویان اینجا نمایش داده می‌شود."/>}</div>
            </div>
        </div>}
    </>;
}

function StudentPanel({exams, results, notifications, loading, reload, view = 'exams'}) {
    const [joinCode, setJoinCode] = useState(() => new URLSearchParams(window.location.search).get('join') || '',);
    const [showJoin, setShowJoin] = useState(() => Boolean(new URLSearchParams(window.location.search).get('join')));
    const [examFilter, setExamFilter] = useState('active');
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [joining, setJoining] = useState(false);
    const [startingId, setStartingId] = useState(null);
    const [activeExam, setActiveExam] = useState(null);
    const [attempt, setAttempt] = useState(null);
    const [answers, setAnswers] = useState({});
    const [index, setIndex] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [selectedResult, setSelectedResult] = useState(null);
    const [resultFilter, setResultFilter] = useState('all');
    const [resultSearch, setResultSearch] = useState('');
    const [message, setMessage] = useState(null);
    const [autosaveStatus, setAutosaveStatus] = useState('idle');
    const [autosaveTick, setAutosaveTick] = useState(0);
    const [clockNow, setClockNow] = useState(() => Date.now());
    const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
    const [privacyShielded, setPrivacyShielded] = useState(false);
    const [showQuestionNav, setShowQuestionNav] = useState(false);
    const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false);
    const [submissionStatus, setSubmissionStatus] = useState('idle');
    const [submissionNotice, setSubmissionNotice] = useState('');
    const [recoveryTick, setRecoveryTick] = useState(0);
    const [submissionRetryAt, setSubmissionRetryAt] = useState(0);
    const [startRetryAt, setStartRetryAt] = useState({});
    const pendingAutosaveRef = useRef({});
    const autosaveInFlightRef = useRef(false);
    const autosaveRetryCountRef = useRef(0);
    const timeLeftRef = useRef(timeLeft);
    const eventThrottleRef = useRef({});
    const submitInFlightRef = useRef(false);
    const autoSubmitAttemptRef = useRef(null);
    const privacyRestoreTimerRef = useRef(null);
    const questionStageRef = useRef(null);
    const recoveryInFlightRef = useRef(false);
    const submissionRetryCountRef = useRef(0);
    timeLeftRef.current = timeLeft;

    useEffect(() => {
        document.body.classList.toggle('exam-focus-mode', Boolean(activeExam));
        if (activeExam) window.scrollTo({top: 0, behavior: 'auto'});
        return () => document.body.classList.remove('exam-focus-mode');
    }, [activeExam]);

    useEffect(() => {
        if (!showQuestionNav) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const closeOnEscape = (event) => event.key === 'Escape' && setShowQuestionNav(false);
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [showQuestionNav]);

    useEffect(() => {
        const updateClock = () => setClockNow(Date.now());
        const timer = window.setInterval(updateClock, 1000);
        document.addEventListener('visibilitychange', updateClock);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', updateClock);
        };
    }, []);

    useEffect(() => {
        if (!loading) setLastUpdatedAt(new Date());
    }, [loading]);

    const recordBrowserEvent = useCallback((eventType, metadata = {}) => {
        if (!activeExam?.proctoring_enabled && !activeExam?.require_fullscreen) return;
        const now = Date.now();
        if (now - (eventThrottleRef.current[eventType] || 0) < 750) return;
        eventThrottleRef.current[eventType] = now;
        const question = activeExam.exam_questions?.[index];
        examsApi.student.recordEvent(activeExam.id, eventType, {
            question_number: question ? index + 1 : null,
            exam_question_id: question?.id || null,
            fullscreen_active: Boolean(document.fullscreenElement),
            client_occurred_at: new Date(now).toISOString(),
            ...metadata,
        }).catch(() => {});
    }, [activeExam, index]);

    useEffect(() => {
        if (!activeExam?.proctoring_enabled && !activeExam?.require_fullscreen) return undefined;
        const onVisibility = () => document.hidden && recordBrowserEvent('tab_hidden', {visibility_state: document.visibilityState});
        const onFullscreen = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
            if (!document.fullscreenElement) recordBrowserEvent('fullscreen_exit');
        };
        const onCopy = () => recordBrowserEvent('copy');
        const onPaste = () => recordBrowserEvent('paste');
        document.addEventListener('visibilitychange', onVisibility);
        document.addEventListener('fullscreenchange', onFullscreen);
        document.addEventListener('copy', onCopy);
        document.addEventListener('paste', onPaste);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            document.removeEventListener('fullscreenchange', onFullscreen);
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('paste', onPaste);
        };
    }, [activeExam, recordBrowserEvent]);

    useEffect(() => {
        if (!activeExam) {
            setPrivacyShielded(false);
            return undefined;
        }

        const protectContent = () => {
            window.clearTimeout(privacyRestoreTimerRef.current);
            setPrivacyShielded(true);
        };
        const revealContent = () => {
            window.clearTimeout(privacyRestoreTimerRef.current);
            privacyRestoreTimerRef.current = window.setTimeout(() => setPrivacyShielded(false), 250);
        };
        const handleVisibility = () => document.hidden ? protectContent() : revealContent();
        const handleBlur = () => {
            protectContent();
            recordBrowserEvent('window_blur', {
                document_hidden: document.hidden,
                visibility_state: document.visibilityState,
            });
        };
        const handleScreenshotShortcut = (event) => {
            const printScreen = event.key === 'PrintScreen' || event.code === 'PrintScreen';
            const metaPressed = event.metaKey || event.getModifierState?.('Meta');
            const snippingShortcut = metaPressed && event.shiftKey
                && (event.code === 'KeyS' || event.key?.toLowerCase() === 's');
            if (!printScreen && !snippingShortcut) return;
            event.preventDefault();
            protectContent();
            recordBrowserEvent('screenshot_shortcut', {
                shortcut: printScreen ? 'print_screen' : 'win_shift_s',
                event_phase: event.type,
                key: event.key,
                code: event.code,
            });
            revealContent();
        };

        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', revealContent);
        window.addEventListener('keydown', handleScreenshotShortcut, true);
        window.addEventListener('keyup', handleScreenshotShortcut, true);
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            window.clearTimeout(privacyRestoreTimerRef.current);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', revealContent);
            window.removeEventListener('keydown', handleScreenshotShortcut, true);
            window.removeEventListener('keyup', handleScreenshotShortcut, true);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [activeExam, recordBrowserEvent]);

    const flushAutosave = useCallback(async () => {
        if (!activeExam || autosaveInFlightRef.current) return;
        const pending = pendingAutosaveRef.current;
        if (!Object.keys(pending).length) return;

        pendingAutosaveRef.current = {};
        autosaveInFlightRef.current = true;
        setAutosaveStatus('saving');
        let saveFailed = false;
        try {
            await examsApi.student.saveAnswers(activeExam.id, Object.values(pending));
            autosaveRetryCountRef.current = 0;
            setAutosaveStatus(Object.keys(pendingAutosaveRef.current).length ? 'pending' : 'saved');
        } catch (error) {
            saveFailed = true;
            pendingAutosaveRef.current = {...pending, ...pendingAutosaveRef.current};
            if (isRetryableSubmissionError(error)) {
                autosaveRetryCountRef.current += 1;
                setAutosaveStatus('error');
            } else {
                autosaveRetryCountRef.current = 0;
                setAutosaveStatus('blocked');
            }
        } finally {
            autosaveInFlightRef.current = false;
            if (!saveFailed && Object.keys(pendingAutosaveRef.current).length) {
                setAutosaveTick((value) => value + 1);
            }
        }
    }, [activeExam]);

    useEffect(() => {
        if (!activeExam || !autosaveTick) return undefined;
        const timer = window.setTimeout(flushAutosave, 1200);
        return () => window.clearTimeout(timer);
    }, [activeExam, autosaveTick, flushAutosave]);

    useEffect(() => {
        if (!activeExam || autosaveStatus !== 'error' || !isOnline || (timeLeftRef.current !== null && timeLeftRef.current <= 0)) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            if ((timeLeftRef.current !== null && timeLeftRef.current <= 0)
                || !Object.keys(pendingAutosaveRef.current).length) return;
            setAutosaveStatus('pending');
            setAutosaveTick((value) => value + 1);
        }, getAutosaveRetryDelay(autosaveRetryCountRef.current));
        return () => window.clearTimeout(timer);
    }, [activeExam, autosaveStatus, isOnline]);

    useEffect(() => {
        if (!activeExam || !attempt?.id) return;
        saveAnswerDraft({
            attemptId: attempt.id,
            examId: activeExam.id,
            dueAt: attempt.due_at,
            answers,
        });
    }, [activeExam, answers, attempt?.due_at, attempt?.id]);

    const updateAnswer = (question, value) => {
        autosaveRetryCountRef.current = 0;
        setAnswers((current) => ({...current, [question.id]: value}));
        pendingAutosaveRef.current[question.id] = {
            exam_question_id: question.id,
            answer_text: value,
            answer_payload: questionOptions(question).length ? {selected: value} : {},
        };
        setAutosaveStatus('pending');
        setAutosaveTick((current) => current + 1);
    };

    const submit = useCallback(async (automatic = false) => {
        if (!activeExam || submitInFlightRef.current) return;
        const attemptId = attempt?.id || `exam-${activeExam.id}`;
        const previousPending = listPendingSubmissions().find((item) => item.attemptId === attemptId);
        if (previousPending?.retryAt > Date.now()) return;
        const payload = activeExam.exam_questions.map((question) => {
            const value = answers[question.id] || '';
            return {
                exam_question_id: question.id,
                answer_text: value,
                answer_payload: questionOptions(question).length ? {selected: value} : {},
            };
        });
        const pendingSubmission = {
            attemptId,
            examId: activeExam.id,
            answers: payload,
            automatic,
        };
        savePendingSubmission({...previousPending, ...pendingSubmission, retryAt: 0});
        if (navigator.onLine === false) {
            setIsOnline(false);
            setSubmissionStatus('waiting_network');
            setSubmissionNotice(automatic
                ? 'زمان آزمون تمام شده اما اینترنت قطع است. پاسخ‌ها روی این دستگاه نگه داشته شده‌اند و پس از وصل‌شدن خودکار ارسال می‌شوند.'
                : 'اینترنت قطع است. درخواست ارسال و پاسخ‌ها روی این دستگاه نگه داشته شده‌اند و پس از وصل‌شدن خودکار ارسال می‌شوند.');
            return;
        }
        submitInFlightRef.current = true;
        setSubmitting(true);
        setSubmissionStatus('sending');
        setSubmissionNotice(automatic ? 'زمان پایان یافت؛ در حال نهایی‌سازی پاسخ‌ها…' : 'در حال ارسال پاسخ‌ها…');
        try {
            const response = await examsApi.student.submit(activeExam.id, payload);
            removePendingSubmission(pendingSubmission.attemptId);
            removeAnswerDraft(pendingSubmission.attemptId);
            submissionRetryCountRef.current = 0;
            setSubmissionRetryAt(0);
            pendingAutosaveRef.current = {};
            setResult(response.attempt);
            setActiveExam(null);
            if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
            const expired = response.submission_state === 'expired_finalized' || response.attempt?.status === 'expired';
            setMessage({
                type: 'success',
                text: expired
                    ? 'زمان آزمون پایان یافت و پاسخ‌های ذخیره‌شده به‌صورت خودکار نهایی شدند.'
                    : automatic ? 'زمان پایان یافت و پاسخ‌ها ارسال شد.' : 'پاسخ‌ها با موفقیت ارسال شد.',
            });
            await reload();
        } catch (error) {
            if (isAlreadyFinalized(error)) {
                removePendingSubmission(pendingSubmission.attemptId);
                removeAnswerDraft(pendingSubmission.attemptId);
                setSubmissionRetryAt(0);
                setActiveExam(null);
                setMessage({type: 'success', text: 'پاسخ‌ها پیش‌تر نهایی شده‌اند.'});
                await reload();
            } else if (isRetryableSubmissionError(error)) {
                const retryCount = (previousPending?.retryCount || submissionRetryCountRef.current) + 1;
                const retryAt = getSubmissionRetryAt(error, retryCount);
                submissionRetryCountRef.current = retryCount;
                savePendingSubmission({...pendingSubmission, retryCount, retryAt});
                setSubmissionRetryAt(retryAt);
                setSubmissionStatus(error?.status === 429 ? 'rate_limited' : 'waiting_network');
                setSubmissionNotice(error?.status === 429
                    ? 'درخواست‌های پیاپی محدود شده‌اند؛ پاسخ‌های شما محفوظ‌اند.'
                    : 'ارسال پاسخ‌ها به سرور ناموفق بود. پاسخ‌ها روی این دستگاه نگه داشته شده‌اند و دوباره ارسال می‌شوند.');
            } else {
                removePendingSubmission(pendingSubmission.attemptId);
                setSubmissionStatus('error');
                setSubmissionNotice(getErrorMessage(error));
            }
        } finally {
            submitInFlightRef.current = false;
            setSubmitting(false);
        }
    }, [activeExam, answers, attempt?.id, reload]);

    useEffect(() => {
        if (!activeExam || !attempt?.due_at) return undefined;
        const updateTimeLeft = () => setTimeLeft(Math.max(0, Math.floor((new Date(attempt.due_at).getTime() - Date.now()) / 1000)));
        updateTimeLeft();
        const timer = window.setInterval(updateTimeLeft, 1000);
        return () => window.clearInterval(timer);
    }, [activeExam, attempt?.due_at]);

    useEffect(() => {
        if (!activeExam || timeLeft === null || timeLeft > 0) return;
        const submissionKey = attempt?.id || activeExam.id;
        if (autoSubmitAttemptRef.current !== submissionKey) {
            autoSubmitAttemptRef.current = submissionKey;
            submit(true);
        }
    }, [activeExam, attempt?.id, submit, timeLeft]);

    useEffect(() => {
        const handleOffline = () => setIsOnline(false);
        const handleOnline = () => {
            setIsOnline(true);
            if (!activeExam) return;
            if (timeLeft > 0 && Object.keys(pendingAutosaveRef.current).length) {
                setAutosaveTick((value) => value + 1);
            }
            if (submissionStatus === 'waiting_network' || submissionStatus === 'rate_limited') submit(timeLeft <= 0);
        };
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [activeExam, submissionStatus, submit, timeLeft]);

    useEffect(() => {
        if (!activeExam || !isOnline || !['waiting_network', 'rate_limited'].includes(submissionStatus)) return undefined;
        const timer = window.setTimeout(() => submit(
            Boolean(attempt?.due_at && new Date(attempt.due_at).getTime() <= Date.now())
        ), Math.max(0, submissionRetryAt - Date.now()));
        return () => window.clearTimeout(timer);
    }, [activeExam, attempt?.due_at, isOnline, submissionRetryAt, submissionStatus, submit]);

    useEffect(() => {
        if (activeExam || !isOnline || recoveryInFlightRef.current) return undefined;
        const pendingItems = listPendingSubmissions();
        if (!pendingItems.length) return undefined;
        let cancelled = false;
        let retryTimer = null;
        recoveryInFlightRef.current = true;
        (async () => {
            let recovered = 0;
            for (const item of pendingItems) {
                if (item.retryAt > Date.now()) continue;
                try {
                    await examsApi.student.submit(item.examId, item.answers);
                    removePendingSubmission(item.attemptId);
                    removeAnswerDraft(item.attemptId);
                    recovered += 1;
                } catch (error) {
                    if (isAlreadyFinalized(error)) {
                        removePendingSubmission(item.attemptId);
                        removeAnswerDraft(item.attemptId);
                        recovered += 1;
                        continue;
                    }
                    if (!isRetryableSubmissionError(error)) removePendingSubmission(item.attemptId);
                    else {
                        const retryCount = (item.retryCount || 0) + 1;
                        savePendingSubmission({...item, retryCount, retryAt: getSubmissionRetryAt(error, retryCount)});
                    }
                    if (!cancelled) setMessage({type: 'error', text: error?.status === 429
                        ? 'درخواست‌های پیاپی محدود شده‌اند؛ پاسخ‌های شما محفوظ‌اند و پس از پایان انتظار دوباره ارسال می‌شوند.'
                        : `بازیابی ارسال قبلی ناموفق بود: ${getErrorMessage(error)}`});
                }
            }
            const remaining = listPendingSubmissions();
            if (remaining.length) retryTimer = window.setTimeout(() => {
                if (!cancelled) setRecoveryTick((value) => value + 1);
            }, Math.max(1000, Math.min(...remaining.map((item) => item.retryAt || Date.now())) - Date.now()));
            if (recovered && !cancelled) {
                setMessage({type: 'success', text: 'پاسخ‌های در انتظار، پس از برقراری ارتباط با موفقیت نهایی شدند.'});
                await reload();
            }
        })().finally(() => { recoveryInFlightRef.current = false; });
        return () => { cancelled = true; if (retryTimer) window.clearTimeout(retryTimer); };
    }, [activeExam, isOnline, recoveryTick, reload]);

    const join = async (event) => {
        event.preventDefault();
        setJoining(true);
        try {
            await examsApi.student.join(joinCode.trim());
            setJoinCode('');
            setMessage({type: 'success', text: 'با موفقیت به آزمون پیوستید.'});
            await reload();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setJoining(false);
        }
    };

    const start = async (exam) => {
        if (startingId !== null || (startRetryAt[exam.id] || 0) > Date.now()) return;
        const timing = getStudentExamTiming(exam, Date.now());
        if (timing !== 'open') {
            const text = timing === 'upcoming'
                ? `این آزمون از ${formatDate(exam.starts_at)} قابل شروع است.`
                : timing === 'ended'
                    ? 'زمان شرکت در این آزمون پایان یافته است.'
                    : 'این آزمون بسته شده و قابل شروع نیست.';
            setMessage({type: 'error', text});
            return;
        }
        setStartingId(exam.id);
        if (exam.require_fullscreen && !document.fullscreenElement && document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen().catch(() => {
                setMessage({type: 'error', text: 'ورود به حالت تمام‌صفحه انجام نشد؛ اجازه مرورگر را بررسی و دوباره تلاش کنید.'});
            });
        }
        try {
            const response = await examsApi.student.start(exam.id);
            autoSubmitAttemptRef.current = null;
            setSubmissionStatus('idle');
            setSubmissionNotice('');
            const pending = listPendingSubmissions().find((item) => item.attemptId === response.attempt.id);
            if (pending?.retryAt > Date.now()) {
                setSubmissionRetryAt(pending.retryAt);
                submissionRetryCountRef.current = pending.retryCount || 0;
                setSubmissionStatus('rate_limited');
                setSubmissionNotice('درخواست‌های پیاپی محدود شده‌اند؛ پاسخ‌های شما محفوظ‌اند.');
            }
            setActiveExam(response.exam);
            setIsFullscreen(Boolean(document.fullscreenElement));
            setAttempt(response.attempt);
            const serverAnswers = Object.fromEntries((response.attempt.answers || []).map((a) => [a.exam_question_id, a.answer_text]));
            const localDraft = loadAnswerDraft(response.attempt.id);
            const localAnswers = localDraft?.examId === response.exam.id ? localDraft.answers : {};
            const restoredAnswers = {...serverAnswers, ...localAnswers};
            setAnswers(restoredAnswers);
            pendingAutosaveRef.current = Object.fromEntries(response.exam.exam_questions
                .filter((question) => Object.prototype.hasOwnProperty.call(localAnswers, question.id))
                .map((question) => [question.id, {
                    exam_question_id: question.id,
                    answer_text: localAnswers[question.id] || '',
                    answer_payload: questionOptions(question).length ? {selected: localAnswers[question.id] || ''} : {},
                }]));
            const hasLocalDraft = Object.keys(pendingAutosaveRef.current).length > 0;
            setAutosaveStatus(hasLocalDraft ? 'pending' : (response.attempt.answers || []).length ? 'saved' : 'idle');
            if (hasLocalDraft) setAutosaveTick((value) => value + 1);
            setIndex(0);
            setTimeLeft(Math.max(0, Math.floor((new Date(response.attempt.due_at).getTime() - Date.now()) / 1000)));
            setResult(null);
        } catch (error) {
            if (error?.status === 429) {
                const retryAt = Date.now() + Math.max(1000, (error.retryAfter || 60) * 1000);
                setStartRetryAt((current) => ({...current, [exam.id]: retryAt}));
            }
            setMessage({type: 'error', text: getErrorMessage(error)});
        } finally {
            setStartingId(null);
        }
    };

    const readNotification = async (notificationId) => {
        try {
            await examsApi.student.readNotification(notificationId);
            await reload();
        } catch (error) {
            setMessage({type: 'error', text: getErrorMessage(error)});
        }
    };

    const current = activeExam?.exam_questions?.[index];
    const answered = activeExam?.exam_questions.filter((q) => (answers[q.id] || '').trim()).length || 0;
    const examViews = useMemo(() => {
        const priority = {open: 0, upcoming: 1, ended: 2, closed: 3};
        return exams.map((exam) => ({exam, timing: getStudentExamTiming(exam, clockNow)}))
            .sort((a, b) => (priority[a.timing] ?? 4) - (priority[b.timing] ?? 4)
                || new Date(a.exam.starts_at).getTime() - new Date(b.exam.starts_at).getTime());
    }, [clockNow, exams]);
    const filteredExamViews = examViews.filter(({timing}) => examFilter === 'all'
        || (examFilter === 'active' && ['open', 'upcoming'].includes(timing))
        || timing === examFilter
        || (examFilter === 'completed' && ['ended', 'closed'].includes(timing)));
    const activeExamCount = examViews.filter(({timing}) => ['open', 'upcoming'].includes(timing)).length;
    const completedExamCount = examViews.filter(({timing}) => ['ended', 'closed'].includes(timing)).length;
    const featuredExamId = examViews.find(({timing}) => ['open', 'upcoming'].includes(timing))?.exam.id;
    const resultGroups = useMemo(() => groupStudentResults(results), [results]);
    const visibleResultGroups = useMemo(() => filterStudentResultGroups(resultGroups, resultFilter, resultSearch), [resultFilter, resultGroups, resultSearch]);
    const refreshStudentData = async () => {
        await reload();
        setLastUpdatedAt(new Date());
    };

    const goToQuestion = (nextIndex) => {
        setIndex(nextIndex);
        setShowQuestionNav(false);
        window.requestAnimationFrame(() => questionStageRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}));
    };

    const confirmSubmit = () => {
        setShowQuestionNav(false);
        if (window.confirm(`آزمون ارسال شود؟ ${activeExam.question_count - answered} سؤال بدون پاسخ است.`)) submit();
    };
    const examFinished = timeLeft !== null && timeLeft <= 0;
    const submissionWaitSeconds = Math.max(0, Math.ceil((submissionRetryAt - clockNow) / 1000));
    const pendingRecovery = listPendingSubmissions().filter((item) => item.retryAt > clockNow);
    const leaveExamView = () => {
        setShowQuestionNav(false);
        setActiveExam(null);
        if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
        setMessage({
            type: submissionStatus === 'waiting_network' ? 'error' : 'success',
            text: submissionStatus === 'waiting_network'
                ? 'ارسال پاسخ‌ها در صف باقی مانده است. این صفحه را روی همین دستگاه باز کنید تا پس از وصل‌شدن اینترنت دوباره ارسال شود.'
                : 'از نمای آزمون خارج شدید.',
        });
        reload();
    };

    if (activeExam && current) return <section className="exam-taking"
        onCopy={(event) => { event.preventDefault(); recordBrowserEvent('copy'); }}
        onCut={(event) => event.preventDefault()}
        onContextMenu={(event) => event.preventDefault()}
        onDragStart={(event) => event.preventDefault()}>
        {privacyShielded && <div className="exam-privacy-shield" role="status" aria-live="polite">
            <FiShield/><strong>محتوای آزمون محافظت شده است</strong><span>برای ادامه به پنجره آزمون بازگردید.</span>
        </div>}
        <div className="exam-capture-watermark" aria-hidden="true">
            {[0, 1, 2, 3].map((item) => <span key={item}>سام · تلاش #{getAttemptNumber(attempt)}</span>)}
        </div>
        <div className="taking-header">
            <div><span>آزمون در حال برگزاری</span><h1>{activeExam.title}</h1>
                <p>{answered} از {activeExam.question_count} سؤال پاسخ داده شده</p>{(activeExam.proctoring_enabled || activeExam.require_fullscreen) && <small className="proctoring-badge"><FiShield/> {activeExam.proctoring_enabled ? 'نظارت مرورگر فعال است' : 'حالت تمام‌صفحه الزامی است'}{activeExam.proctoring_enabled && activeExam.require_fullscreen ? ' · حالت تمام‌صفحه الزامی است' : ''}</small>}</div>
            <button type="button" className="taking-mobile-index" onClick={() => setShowQuestionNav(true)} aria-label="باز کردن فهرست سؤال‌ها"><FiList/><span>سؤال</span><strong>{index + 1}/{activeExam.question_count}</strong></button>
            <div className={`taking-timer ${timeLeft < 300 ? 'danger' : ''}`}>
                <FiClock/><span>زمان باقی‌مانده</span><strong>{formatDuration(timeLeft)}</strong></div>
        </div>
        <div className="taking-progress"><i style={{width: `${(answered / activeExam.question_count) * 100}%`}}/></div>
        {(!isOnline || examFinished || submissionStatus !== 'idle') && <div className={`exam-submission-alert ${submissionStatus === 'error' ? 'error' : examFinished ? 'finished' : 'offline'}`} role="alert" aria-live="assertive">
            <span>{submitting ? <FiLoader className="spin"/> : !isOnline ? <FiWifiOff/> : <FiAlertCircle/>}</span>
            <div><strong>{submitting ? 'در حال نهایی‌سازی آزمون' : submissionStatus === 'rate_limited' ? 'محدودیت درخواست' : !isOnline ? 'اتصال اینترنت قطع است' : examFinished ? 'زمان آزمون پایان یافته است' : 'وضعیت ارسال'}</strong><p>{submissionNotice || (!isOnline ? 'تغییرات پاسخ‌ها روی این دستگاه باقی می‌مانند. پس از وصل‌شدن، ذخیره و ارسال دوباره انجام می‌شود.' : 'پاسخ‌های ذخیره‌شده در حال نهایی‌شدن هستند.')}{submissionStatus === 'rate_limited' && submissionWaitSeconds > 0 ? ` تلاش بعدی تا ${formatDuration(submissionWaitSeconds)} دیگر.` : ''}</p></div>
            <div className="exam-submission-actions">
                {(['waiting_network', 'rate_limited', 'error'].includes(submissionStatus)) && <button type="button" className="exam-btn primary" disabled={!isOnline || submitting || submissionWaitSeconds > 0} onClick={() => submit(examFinished)}><FiRefreshCw/> تلاش دوباره{submissionWaitSeconds > 0 ? ` (${formatDuration(submissionWaitSeconds)})` : ''}</button>}
                {examFinished && !submitting && <button type="button" className="exam-btn ghost" onClick={leaveExamView}>بازگشت به آزمون‌ها</button>}
            </div>
        </div>}
        <div className="taking-layout">
            {showQuestionNav && <button type="button" className="question-nav-backdrop" aria-label="بستن فهرست سؤال‌ها" onClick={() => setShowQuestionNav(false)}/>}
            <aside className={`question-nav ${showQuestionNav ? 'open' : ''}`} aria-label="فهرست سؤال‌های آزمون">
                <header><strong>فهرست سؤال‌ها</strong><button type="button" onClick={() => setShowQuestionNav(false)} aria-label="بستن"><FiX/></button></header>
                <div>{activeExam.exam_questions.map((q, i) => <button key={q.id}
                                                                      className={`${index === i ? 'active' : ''} ${(answers[q.id] || '').trim() ? 'answered' : ''}`}
                                                                      onClick={() => goToQuestion(i)}>{i + 1}</button>)}</div>
                <footer><small>تلاش #{getAttemptNumber(attempt)} · {answered} پاسخ ثبت‌شده</small><button type="button" className="exam-btn success" disabled={submitting || examFinished} onClick={confirmSubmit}><FiSend/> ارسال آزمون</button></footer></aside>
            <article className="question-stage" ref={questionStageRef}>
                {activeExam.require_fullscreen && !isFullscreen && <div className="fullscreen-warning"><FiAlertCircle/><span>برای ادامه امن آزمون، دوباره وارد حالت تمام‌صفحه شوید.</span><button type="button" onClick={() => document.documentElement.requestFullscreen?.().catch(() => {})}>ورود به تمام‌صفحه</button></div>}
                <div className="question-top">
                    <span>سؤال {index + 1} از {activeExam.question_count}</span><b>{current.points} نمره</b></div>
                {current.node_name && <small className="question-topic" dir={getTextDirection(current.node_name)}>{current.node_name}</small>}
                <h2 className="question-prompt" dir={getTextDirection(current.prompt)}><MathText text={current.prompt}/></h2>
                {questionOptions(current).length ?
                    <div className="answer-options">{questionOptions(current).map((option) => <button type="button"
                                                                                                      key={option}
                                                                                                      dir={getTextDirection(option)}
                                                                                                      disabled={examFinished || submitting}
                                                                                                      className={answers[current.id] === option ? 'selected' : ''}
                                                                                                      onClick={() => updateAnswer(current, answers[current.id] === option ? '' : option)}>
                        <i>{answers[current.id] === option && <FiCheck/>}</i><span><MathText text={option}/></span></button>)}</div> :
                    <textarea className="answer-text" dir="auto" rows="8" placeholder="پاسخ خود را بنویسید..."
                              disabled={examFinished || submitting}
                              value={answers[current.id] || ''}
                              onChange={(e) => updateAnswer(current, e.target.value)}/>}
                <div className="question-actions">
                    <button className="exam-btn ghost" disabled={!index} onClick={() => goToQuestion(index - 1)}>
                        <FiArrowRight/> قبلی
                    </button>
                    <button type="button" className="exam-btn ghost mobile-question-list" onClick={() => setShowQuestionNav(true)} aria-label="فهرست سؤال‌ها"><FiList/><span>{index + 1}/{activeExam.question_count}</span></button>
                    {index < activeExam.question_count - 1 ?
                        <button className="exam-btn primary" onClick={() => goToQuestion(index + 1)}>بعدی <FiArrowLeft/>
                        </button> : <button className="exam-btn success" disabled={submitting || examFinished}
                                            onClick={confirmSubmit}>{submitting ?
                            <FiLoader className="spin"/> : <FiSend/>} ارسال آزمون</button>}</div>
                <div className={`autosave-status ${autosaveStatus}`}>
                    {autosaveStatus === 'saving' && <><FiLoader className="spin"/> در حال ذخیره پاسخ‌ها...</>}
                    {autosaveStatus === 'pending' && <><FiSave/> ذخیره خودکار تا لحظاتی دیگر</>}
                    {autosaveStatus === 'saved' && <><FiCheck/> پاسخ‌ها ذخیره شدند</>}
                    {autosaveStatus === 'error' && <><FiAlertCircle/> ذخیره خودکار ناموفق بود؛ چند لحظه دیگر دوباره تلاش می‌کنیم.</>}
                    {autosaveStatus === 'blocked' && <><FiAlertCircle/> ذخیره پاسخ پذیرفته نشد؛ پاسخ‌ها روی این دستگاه باقی مانده‌اند.</>}
                </div>
            </article>
        </div>
    </section>;

    return <>
        <Message message={message}/>
        {pendingRecovery.length > 0 && <div className="exam-message error" role="status"><FiClock/><span>درخواست‌های پیاپی محدود شده‌اند؛ پاسخ‌های شما محفوظ‌اند. ارسال دوباره تا {formatDuration(Math.min(...pendingRecovery.map((item) => Math.ceil((item.retryAt - clockNow) / 1000))))} دیگر انجام می‌شود.</span></div>}
        {view === 'exams' && result && <section className="result-banner"><span><FiCheck/></span>
            <div><h3>آزمون ارسال شد</h3><p>{result.score === null || result.score === undefined
                ? 'نتیجه و نمره پس از بسته‌شدن آزمون منتشر می‌شود.'
                : `نمره: ${formatNumber(result.score)} از ${formatNumber(result.max_score)}`}</p></div>
        </section>}
        {view === 'exams' && <section className="exam-panel student-exams-panel">
            <div className="exam-panel-header">
                <div><span className="student-section-kicker">اقدام بعدی</span><h2>آزمون‌های من</h2><p>{activeExamCount
                    ? `${formatNumber(activeExamCount)} آزمون فعال یا آینده دارید؛ نزدیک‌ترین مورد بالاتر نمایش داده می‌شود.`
                    : 'فعلاً آزمون فعال یا آینده‌ای ندارید.'}</p></div>
                <div className="student-refresh-group">
                    {lastUpdatedAt && <small>آخرین دریافت {lastUpdatedAt.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'})}</small>}
                    <button className="exam-btn ghost" onClick={refreshStudentData} disabled={loading}><FiRefreshCw
                    className={loading ? 'spin' : ''}/> به‌روزرسانی
                    </button>
                </div>
            </div>
            <div className="exam-filter-tabs" role="group" aria-label="فیلتر آزمون‌ها">
                {[
                    ['active', `فعال و آینده (${formatNumber(activeExamCount)})`],
                    ['open', 'در حال برگزاری'],
                    ['upcoming', 'آینده'],
                    ['completed', `سوابق (${formatNumber(completedExamCount)})`],
                ].map(([value, label]) => <button key={value} type="button" aria-pressed={examFilter === value}
                    className={examFilter === value ? 'active' : ''}
                    onClick={() => setExamFilter(value)}>{label}</button>)}
            </div>
            {loading ?
                <div className="exam-loading"><FiLoader className="spin"/> در حال دریافت...</div> : filteredExamViews.length ?
                    <div className="student-list" id="student-exam-list">{filteredExamViews.map(({exam, timing}) => {
                        const timingCopy = studentExamTimingCopy[timing];
                        const {canStart, canResume, attemptsExhausted} = getStudentExamAvailability(exam, timing, results);
                        const timeCaption = attemptsExhausted
                            ? 'شما همه تلاش‌های مجاز را انجام داده‌اید'
                            : canResume
                                ? `تلاش در حال انجام · مهلت تا ${formatDate(exam.ends_at)}`
                            : timing === 'upcoming'
                            ? `شروع در ${formatDate(exam.starts_at)}`
                            : timing === 'open'
                                ? `مهلت تا ${formatDate(exam.ends_at)}`
                                : timing === 'ended'
                                    ? `پایان در ${formatDate(exam.ends_at)}`
                                    : 'این آزمون بسته شده است';
                        return <article key={exam.id} className={`student-exam-card ${timing} ${exam.id === featuredExamId ? 'featured' : ''}`}>
                        <div className="exam-date">
                            <strong>{new Date(exam.starts_at).toLocaleDateString('fa-IR', {day: '2-digit'})}</strong><span>{new Date(exam.starts_at).toLocaleDateString('fa-IR', {month: 'long'})}</span>
                        </div>
                        <div className="student-info"><span className={`exam-status ${timing}`}>{timingCopy.label}</span>
                            <h3>{exam.title}</h3><p>{exam.description || 'بدون توضیحات'}</p>
                            <div>
                                <span><FiList/> {formatNumber(exam.question_count)} سؤال</span><span><FiClock/> {formatNumber(exam.duration_minutes)} دقیقه</span><span><FiHash/> {typeLabels[exam.question_type]}</span><span><FiRotateCcw/> تا {formatNumber(exam.max_attempts || 1)} تلاش</span>{exam.proctoring_enabled && <span><FiShield/> نظارت فعال</span>}
                            </div>
                        </div>
                        <div className="student-action"><small>{timeCaption}</small>
                            {canStart ? <button disabled={startingId === exam.id || (startRetryAt[exam.id] || 0) > clockNow}
                                onClick={() => start(exam)}>{startingId === exam.id ? <FiLoader className="spin"/> : <FiPlay/>} {(startRetryAt[exam.id] || 0) > clockNow ? `تلاش دوباره تا ${formatDuration(Math.ceil((startRetryAt[exam.id] - clockNow) / 1000))}` : canResume ? 'ادامه آزمون' : timingCopy.action}
                            </button> : <span className="student-action-state">{attemptsExhausted ? 'سقف تلاش تکمیل شده' : timingCopy.action}</span>}
                        </div>
                    </article>})}</div> : <EmptyState
                        title={examFilter === 'active' && exams.length ? 'فعلاً آزمون فعالی ندارید' : exams.length ? 'آزمونی با این وضعیت وجود ندارد' : 'آزمونی به شما تخصیص داده نشده'}
                        text={exams.length ? 'برای مشاهده آزمون‌های قبلی، سوابق را باز کنید.' : 'آزمون‌های منتشرشده پس از تخصیص مدرس در این بخش نمایش داده می‌شوند.'}
                        action={examFilter === 'active' && completedExamCount > 0 ? <button type="button" className="exam-btn ghost" onClick={() => setExamFilter('completed')}>مشاهده سوابق</button> : null}
                    />}
        </section>}
        {view === 'exams' && <section className={`student-join ${showJoin ? 'open' : ''}`}>
            <button type="button" className="student-join-toggle" onClick={() => setShowJoin((current) => !current)}
                    aria-expanded={showJoin} aria-controls="student-join-form">
                <span><FiLink/><span><strong>ورود با کد آزمون</strong><small>کدی را که مدرس در اختیار شما گذاشته وارد کنید.</small></span></span>
                <FiChevronDown aria-hidden="true"/>
            </button>
            {showJoin && <form id="student-join-form" onSubmit={join}>
                <label className="sr-only" htmlFor="student-join-code">کد آزمون</label>
                <input id="student-join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value)}
                       placeholder="کد آزمون" autoComplete="off"/>
                <button type="submit" disabled={joining || !joinCode.trim()}>{joining ? <FiLoader className="spin"/> :
                    <FiLink/>} پیوستن
                </button>
            </form>}
        </section>}
        {view === 'exams' && notifications.length > 0 && <section className="exam-panel notification-panel">
            <div className="exam-panel-header"><div><h2>اعلان‌های آزمون</h2><p>{notifications.filter((item) => !item.read_at).length} اعلان خوانده‌نشده</p></div><FiBell/></div>
            <div className="notification-list">{notifications.map((item) => <article key={item.id} className={item.read_at ? 'read' : ''}>
                <span><FiBell/></span><div><strong>{notificationLabels[item.kind] || item.kind} · {item.exam_title}</strong><p>{item.message}</p><small>{formatDate(item.created_at)}</small></div>
                {!item.read_at && <button className="exam-btn ghost" onClick={() => readNotification(item.id)}><FiCheck/> خواندم</button>}
            </article>)}</div>
        </section>}
        {view === 'results' && <section className="exam-panel student-results-panel">
            <div className="exam-panel-header">
                <div><h2>نتایج و نمره‌های من</h2><p>هر آزمون یک‌بار نمایش داده می‌شود و تلاش‌های مختلف داخل آن قرار دارند.</p></div>
                <button className="exam-btn ghost" onClick={refreshStudentData} disabled={loading}><FiRefreshCw className={loading ? 'spin' : ''}/> به‌روزرسانی</button>
            </div>
            <div className="student-result-toolbar">
                <label><FiSearch/><input value={resultSearch} onChange={(event) => setResultSearch(event.target.value)} placeholder="جست‌وجوی نام آزمون" aria-label="جست‌وجوی نتایج"/></label>
                <div role="group" aria-label="فیلتر نتایج">{[['all', 'همه'], ['released', 'منتشرشده'], ['pending', 'در انتظار انتشار']].map(([value, label]) => <button key={value} type="button" className={resultFilter === value ? 'active' : ''} aria-pressed={resultFilter === value} onClick={() => setResultFilter(value)}>{label}</button>)}</div>
            </div>
            {visibleResultGroups.length ? <div className="student-results-list">{visibleResultGroups.map((group) => {
                const item = group.latestAttempt;
                const displayAttempt = item.score == null && group.bestAttempt ? group.bestAttempt : item;
                const gradingState = getResultGradingState(displayAttempt);
                const resultReleased = displayAttempt.score !== null && displayAttempt.score !== undefined;
                const percentage = resultReleased && Number(displayAttempt.max_score) > 0 ? Math.round((Number(displayAttempt.score) / Number(displayAttempt.max_score)) * 100) : null;
                return <article key={group.key}>
                    <div className={`result-score ${resultReleased ? '' : 'locked'}`}><strong>{resultReleased ? formatNumber(displayAttempt.score) : <FiLock/>}</strong><span>{resultReleased ? `از ${formatNumber(displayAttempt.max_score)}` : 'منتظر انتشار'}</span>{resultReleased && <small>{formatNumber(percentage)}٪</small>}</div>
                    <div className="result-info"><span className={`exam-status ${resultReleased ? 'published' : 'draft'}`}>{resultReleased ? gradingState.label : 'نتیجه هنوز منتشر نشده'}</span><h3>{group.examTitle}</h3><p>{formatNumber(group.attempts.length)} تلاش · آخرین فعالیت: {formatDate(item.updated_at || item.submitted_at)}{group.bestAttempt && group.attempts.length > 1 ? ` · بهترین نمره: ${formatNumber(group.bestAttempt.score)}` : ''}</p></div>
                    <button className="exam-btn ghost" onClick={() => setSelectedResult(group)}><FiEye/> مشاهده تلاش‌ها و پاسخ‌ها</button>
                </article>;
            })}</div> : <EmptyState title={results.length ? 'نتیجه‌ای با این فیلتر پیدا نشد' : 'هنوز نتیجه‌ای ثبت نشده'} text={results.length ? 'فیلتر یا عبارت جست‌وجو را تغییر دهید.' : 'پس از ارسال آزمون، نمره خودکار یا نمره ثبت‌شده توسط استاد اینجا نمایش داده می‌شود.'}/>}
        </section>}
        {view === 'results' && selectedResult && <StudentResultDetails group={selectedResult} onClose={() => setSelectedResult(null)} onChanged={reload}/>}
        {view === 'requests' && <StudentFeedbackHistory standalone onResultRefresh={reload}/>}
    </>;
}

export default function ExamPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const {role, isPlatformAdmin, isRoleLoading} = useAuthRole();
    const [exams, setExams] = useState([]);
    const [results, setResults] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!role) return;
        setLoading(true);
        setError('');
        try {
            if (role === 'teacher') {
                setLoading(false);
                return;
            } else {
                const [studentExams, studentResults, notificationItems] = await Promise.all([
                    examsApi.student.list(),
                    examsApi.student.results(),
                    examsApi.student.notifications(),
                ]);
                setExams(toArray(studentExams));
                setResults(toArray(studentResults));
                setNotifications(toArray(notificationItems));
            }
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [role]);
    useEffect(() => {
        load();
    }, [load]);

    const studentView = location.pathname.endsWith('/results')
        ? 'results'
        : location.pathname.endsWith('/requests') ? 'requests' : 'exams';
    const studentHeader = {
        exams: {
            title: 'آزمون‌های من',
            description: 'آزمون‌های فعال و آینده را ببینید، با کد وارد شوید و آزمون خود را شروع کنید.',
        },
        results: {
            title: 'نتایج و نمره‌ها',
            description: 'نمره‌ها و جزئیات پاسخ‌ها را بدون پیمایش بخش‌های دیگر بررسی کنید.',
        },
        requests: {
            title: 'بازخورد و اعتراض‌ها',
            description: 'وضعیت بازخوردها، اعتراض‌ها و نتیجه رسیدگی استاد را در یک بخش پیگیری کنید.',
        },
    }[studentView];

    return <main className="exam-page">
        <section className={`exam-hero app-page-header ${role === 'student' ? `student-hero ${studentView !== 'exams' ? 'compact' : ''}` : ''}`}>
            <div><span className="exam-eyebrow">{role === 'student' ? <><FiPlay/> فضای آزمون دانشجو</> : <>
                <FiEdit3/> مرکز مدیریت آزمون</>}</span>
                <h1>{role === 'student' ? studentHeader.title : 'آزمون‌ها را بسازید و مدیریت کنید'}</h1>
                <p>{role === 'student' ? studentHeader.description : 'از سؤال‌های آماده استفاده کنید، آزمون را منتشر کنید و تلاش‌های دانشجویان را ببینید.'}</p>
            </div>
            {role === 'teacher' ? <div className="exam-hero-actions app-page-header-actions">
                <button type="button" className="exam-hero-action primary" onClick={() => navigate('/exampage/create')}><FiPlus/> ساخت دستی آزمون</button>
                {isPlatformAdmin && <button type="button" className="exam-hero-action secondary" onClick={() => navigate('/exam-planner')}><FiZap/> ساخت هوشمند آزمون</button>}
            </div> : <div className="exam-hero-mark"><FiFileText/><span>EXAM</span></div>}
        </section>
        {error && <Message message={{type: 'error', text: error}}/>}
        {!error && role === 'teacher' &&
            <TeacherExamPanel/>}
        {!error && role === 'student' && <StudentPanel exams={exams} results={results} notifications={notifications} loading={loading} reload={load} view={studentView}/>}
        {!error && isRoleLoading &&
            <div className="exam-loading"><FiLoader className="spin"/> در حال تشخیص دسترسی...</div>}
    </main>;
}
